import { useState } from "react";
import {
  LoginRequestSchema,
  RegisterRequestSchema,
  ForgotPasswordRequestSchema,
} from "@wooflit/shared";
import { api, ApiError } from "../lib/api";
import { useAuthStore } from "../stores/authStore";

type Mode = "login" | "register" | "forgot-password";

export function LoginPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const login = useAuthStore((state) => state.login);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setBusinessName("");
    setError("");
    setSuccess("");
  };

  const switchMode = (newMode: Mode) => {
    resetForm();
    setMode(newMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate input before API call
    if (mode === "login") {
      const result = LoginRequestSchema.safeParse({ email, password });
      if (!result.success) {
        setError(result.error.issues[0]?.message ?? "Invalid input");
        return;
      }
    } else if (mode === "register") {
      const result = RegisterRequestSchema.safeParse({
        email,
        password,
        name,
        tenantName: businessName,
      });
      if (!result.success) {
        setError(result.error.issues[0]?.message ?? "Invalid input");
        return;
      }
    } else if (mode === "forgot-password") {
      const result = ForgotPasswordRequestSchema.safeParse({ email });
      if (!result.success) {
        setError(result.error.issues[0]?.message ?? "Invalid input");
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === "login") {
        const { token, user } = await api.auth.login(email, password);
        login(token, user);
      } else if (mode === "register") {
        const { token, user } = await api.auth.register(
          email,
          password,
          name,
          businessName
        );
        login(token, user);
      } else if (mode === "forgot-password") {
        await api.auth.forgotPassword(email);
        setSuccess("Check your email for password reset instructions.");
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-bold text-gray-900">
          Wooflit
        </h1>
        <h2 className="mt-2 text-center text-xl text-gray-600">
          {mode === "login" && "Sign in to your account"}
          {mode === "register" && "Create your account"}
          {mode === "forgot-password" && "Reset your password"}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {mode === "register" && (
              <>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Your name
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="businessName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Business name
                  </label>
                  <input
                    id="businessName"
                    type="text"
                    required
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  />
                </div>
              </>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>

            {mode !== "forgot-password" && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                />
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span>Loading...</span>
              ) : (
                <>
                  {mode === "login" && "Sign in"}
                  {mode === "register" && "Create account"}
                  {mode === "forgot-password" && "Send reset link"}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            {mode === "login" && (
              <>
                <button
                  type="button"
                  onClick={() => switchMode("forgot-password")}
                  className="text-amber-600 hover:text-amber-500"
                >
                  Forgot your password?
                </button>
                <p className="mt-4 text-gray-600">
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("register")}
                    className="text-amber-600 hover:text-amber-500 font-medium"
                  >
                    Sign up
                  </button>
                </p>
              </>
            )}
            {mode === "register" && (
              <p className="text-gray-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-amber-600 hover:text-amber-500 font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
            {mode === "forgot-password" && (
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="text-amber-600 hover:text-amber-500"
              >
                Back to sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
